/**
 * LeaveRegistry — standalone Leave administration module (Jul 2026).
 *
 * First-class HR nav item (Admin / HR Manager / HR Specialist) that hosts
 * the full Leave Management module directly — no need to route through
 * My Workspace. My Workspace keeps only the employee self-service tabs
 * (Apply Leave, My Leaves, Calendar, Holidays) for these roles.
 *
 * Renders at /admin/hr/leave-registry — supports ?subtab= deep links.
 */
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import { getUserFromToken } from '../../utils/adminApi';
import HolidayCalendarManager from './HolidayCalendarManager';
import LeavePolicyManager from './LeavePolicyManager';
import LeaveReports from './LeaveReports';
import BalanceAdjustments from './BalanceAdjustments';

// HR-CLEANUP-001 (Jul 2026): self-service tabs (Apply/My Leaves/Calendar)
// and Approvals/Availability removed from the registry — approvals moved
// back to My Workspace for Admin/HR Manager. Registry is admin-config only.
const HR_ROLES = ['admin', 'hr_manager'];
const HR_VIEW_ROLES = ['admin', 'hr_manager', 'hr_specialist'];

const SUBTABS = [
  { id: 'holidays', label: 'Holidays', roles: HR_VIEW_ROLES },
  { id: 'policies', label: 'Policies', roles: HR_ROLES },
  { id: 'reports', label: 'Reports', roles: HR_VIEW_ROLES },
  { id: 'adjustments', label: 'Adjustments', roles: HR_VIEW_ROLES },
];

export default function LeaveRegistry() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = getUserFromToken();
  const role = currentUser?.role;

  const visibleTabs = SUBTABS.filter(s => !s.roles || s.roles.includes(role));

  const getInitial = () => {
    const param = searchParams.get('subtab');
    if (param && visibleTabs.some(s => s.id === param)) return param;
    return 'holidays';
  };
  const [subTab, setSubTab] = useState(getInitial);

  const handleTabChange = (id) => {
    setSubTab(id);
    setSearchParams({ subtab: id }, { replace: true });
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="leave-registry-page">
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
        <div className="px-8 py-6 bg-gradient-to-r from-blue-600 to-blue-700">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Calendar className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Leave Registry</h2>
              <p className="text-blue-100 mt-0.5">Central leave administration — holidays, policies, reports & adjustments</p>
            </div>
          </div>
        </div>

        <div className="border-b border-slate-200 px-4 overflow-x-auto">
          <nav className="flex gap-0.5 min-w-max">
            {visibleTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                data-testid={`registry-subtab-${tab.id}`}
                className={`px-3 py-4 text-sm font-semibold border-b-2 whitespace-nowrap shrink-0 transition-all ${
                  subTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {subTab === 'holidays' && HR_VIEW_ROLES.includes(role) && <HolidayCalendarManager />}
          {subTab === 'policies' && HR_ROLES.includes(role) && <LeavePolicyManager />}
          {subTab === 'reports' && HR_VIEW_ROLES.includes(role) && <LeaveReports />}
          {subTab === 'adjustments' && HR_VIEW_ROLES.includes(role) && <BalanceAdjustments />}
        </div>
      </div>
    </div>
  );
}
